import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { useAdminUsers } from '@/hooks/admin/useAdminUsers';
import { useClientGroups } from '@/hooks/useClientGroups';
import {
  AdminConversation,
  getConversationReadState,
  listAdminConversations,
  listConversationViews,
} from '@/services/api/adminConversations';

export const ADMIN_CONVERSATIONS_KEY = ['admin', 'conversations'];

/**
 * Every advisor–client thread on the platform, for the Messages oversight console (Admin/SuperAdmin = the brokers).
 * Reads GET /admin/conversations, paging through it in the background. Until that endpoint is deployed (or if it
 * fails), falls back to the client groups list the platform already serves, so the console still shows every thread,
 * just without last-message previews, counts or the awaiting-reply flag (`degraded`).
 */
export const useAdminConversations = () => {
  const query = useInfiniteQuery({
    queryKey: ADMIN_CONVERSATIONS_KEY,
    queryFn: ({ pageParam }: { pageParam?: string }) => listAdminConversations({ limit: 100, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 15_000,
    refetchInterval: 30_000,
    retry: false,
  });

  useEffect(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) query.fetchNextPage();
  }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  const degraded = query.isError;
  const { groups, isLoading: groupsLoading } = useClientGroups();
  const { users } = useAdminUsers();

  const fallback = useMemo((): AdminConversation[] => {
    if (!degraded) return [];
    const byId = new Map(users.map((u) => [u.userId, u]));
    return groups.map((g) => {
      const advisor = byId.get(g.advisorId);
      return {
        groupId: g.id,
        holdingName: g.holdingName,
        status: g.status === 'archived' ? 'archived' : 'active',
        advisor: {
          userId: g.advisorId,
          name: advisor?.name || advisor?.email || 'Advisor',
          photoUrl: advisor?.profile?.photoUrl,
        },
        coAdvisors: (g.coAdvisorIds ?? []).map((id) => ({ userId: id, name: byId.get(id)?.name || 'Co-advisor' })),
        participants: g.members.map((m) => ({
          userId: m.userId,
          name: m.name || m.email || 'Member',
          email: m.email,
          role: m.role,
          photoUrl: m.userId ? byId.get(m.userId)?.profile?.photoUrl : undefined,
        })),
        messageCount: 0,
        awaitingReply: false,
      };
    });
  }, [degraded, groups, users]);

  const conversations = useMemo(
    () => (degraded ? fallback : (query.data?.pages.flatMap((p) => p.conversations) ?? [])),
    [degraded, fallback, query.data],
  );

  return {
    conversations,
    degraded,
    isLoading: degraded ? groupsLoading : query.isLoading,
    refetch: query.refetch,
  };
};

export const useConversationReadState = (groupId?: string, enabled = true) =>
  useQuery({
    queryKey: [...ADMIN_CONVERSATIONS_KEY, groupId, 'read-state'],
    queryFn: () => getConversationReadState(groupId as string),
    enabled: !!groupId && enabled,
    retry: false,
    staleTime: 30_000,
  });

export const useConversationViews = (groupId?: string, enabled = true) =>
  useQuery({
    queryKey: [...ADMIN_CONVERSATIONS_KEY, groupId, 'views'],
    queryFn: () => listConversationViews(groupId as string),
    enabled: !!groupId && enabled,
    retry: false,
    staleTime: 30_000,
  });
